import { useCallback, useMemo, type FormEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { IconButton, PlusIcon, toast } from "@carbon6/c6-frontend-design";
import { GnbMenu } from "@/app/shell/Gnb";
import { PageBase, type PageBaseProps } from "@/app/shell/PageBase";
import type { ViewMode } from "@/shared/ui/ViewModeToggle";
import { hideDataset, registerDataset, showDataset } from "../api/datasetApi";
import type { Dataset } from "../domain/dataset";
import {
  readDatasetFilters,
  writeDatasetFilters,
  type DatasetFilters,
} from "../feature/datasetFilters";
import { resolveDatasetErrorMessage } from "../feature/datasetFormatters";
import { readDatasetViewMode, writeDatasetViewMode } from "../feature/datasetViewMode";
import { subscribeProbeChanges } from "../feature/probeChanges";
import { checkSourceRef, suggestDisplayName } from "../feature/sourceRef";
import { DatasetTableSource } from "./DatasetTableSource";
import {
  CLOSED_DIALOG,
  EMPTY_REGISTER_FIELD_ERRORS,
  emptyRegisterDialog,
  type DatasetListDialogState,
  type RegisterFieldErrors,
} from "./DatasetListPageTypes";
import { DatasetFilterBar } from "./components/DatasetFilterBar";
import { DatasetListBody } from "./components/DatasetListBody";
import { DatasetRowActionsMenu } from "./components/DatasetRowActionsMenu";
import { HideDatasetModal } from "./components/HideDatasetModal";
import { RegisterDatasetModal } from "./components/RegisterDatasetModal";
import { openDatasetDetailWindow } from "./datasetDetailWindow";

/** Sampled Dataset 목록의 조회 축은 URL 이 소유한다. */
export function DatasetListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readDatasetFilters(searchParams), [searchParams]);

  const handleFiltersChange = useCallback(
    (next: DatasetFilters) => {
      setSearchParams((prev) => writeDatasetFilters(prev, next), { replace: true });
    },
    [setSearchParams],
  );

  // 상세는 전체화면 창으로 연다 — 목록에 머문 채 여러 Dataset 을 나란히 볼 수 있고, 창은 최소화해도
  // 상태를 보존한다. 딥링크(`/datasets/:uuid`)는 route 페이지가 따로 받는다.
  const handleOpenDataset = useCallback((dataset: Dataset) => {
    openDatasetDetailWindow({ datasetUuid: dataset.uuid, displayName: dataset.displayName });
  }, []);

  return (
    <DatasetListPageView
      bodyFlushTop
      currentMenu={GnbMenu.Datasets}
      filters={filters}
      pageTitle="Sampled Dataset"
      onFiltersChange={handleFiltersChange}
      onOpenDataset={handleOpenDataset}
    />
  );
}

type DatasetListPageViewProps = PageBaseProps & {
  filters: DatasetFilters;
  onFiltersChange: (next: DatasetFilters) => void;
  onOpenDataset: (dataset: Dataset) => void;
};

type DatasetListPageViewState = {
  /** 카드 그리드 · 표. 브라우저가 기억하는 표시 취향이라 URL 축이 아니다. */
  viewMode: ViewMode;
  dialog: DatasetListDialogState;
  /** 숨김 확인 대상. `null` 이면 확인 모달이 닫혀 있다. */
  hideTarget: Dataset | null;
  hideErrorMessage: string | null;
  hideSubmitting: boolean;
};

class DatasetListPageView extends PageBase<DatasetListPageViewProps, DatasetListPageViewState> {
  // 표 소스는 페이지 수명 동안 사는 인스턴스 자원이다 — 렌더마다 새로 만들면 조회가 반복된다.
  private readonly source = new DatasetTableSource({
    onOpen: (dataset) => this.props.onOpenDataset(dataset),
    renderActions: (dataset) => this.renderRowActions(dataset),
    resolveErrorMessage: resolveDatasetErrorMessage,
  });

  state: DatasetListPageViewState = {
    viewMode: readDatasetViewMode(),
    dialog: CLOSED_DIALOG,
    hideTarget: null,
    hideErrorMessage: null,
    hideSubmitting: false,
  };

  private unsubscribeProbeChanges: (() => void) | null = null;

  protected override async initPage(): Promise<void> {
    // 상세 창에서 요청하거나 바뀐 Probe 를 그 행에 반영한다. 목록을 다시 받지 않는다.
    this.unsubscribeProbeChanges = subscribeProbeChanges(
      (change) => void this.source.applyProbeChange(change),
    );
    await this.source.applyFilters(this.props.filters);
  }

  protected override disposePage(): void {
    this.unsubscribeProbeChanges?.();
    this.unsubscribeProbeChanges = null;
    this.source.dispose();
  }

  override componentDidUpdate(previous: DatasetListPageViewProps): void {
    const axisChanged =
      previous.filters.search !== this.props.filters.search ||
      previous.filters.includeHidden !== this.props.filters.includeHidden;
    if (axisChanged) {
      void this.source.applyFilters(this.props.filters);
    }
  }

  protected override renderBelowTabs(): ReactNode {
    return <DatasetFilterBar filters={this.props.filters} onChange={this.props.onFiltersChange} />;
  }

  protected override renderHeaderActions(): ReactNode {
    return (
      <IconButton
        text="Dataset 등록"
        tone="primary"
        variant="solid"
        onClick={this.openRegisterDialog}
      >
        <PlusIcon />
      </IconButton>
    );
  }

  protected override renderContent(): ReactNode {
    const { dialog, hideErrorMessage, hideSubmitting, hideTarget, viewMode } = this.state;

    return (
      <>
        <DatasetListBody
          filters={this.props.filters}
          source={this.source}
          viewMode={viewMode}
          onRefresh={this.handleRefresh}
          onViewModeChange={this.handleViewModeChange}
        />
        {dialog.kind === "register" ? (
          <RegisterDatasetModal
            dialog={dialog}
            onClose={this.closeRegisterDialog}
            onDisplayNameChange={this.handleDisplayNameChange}
            onSourceRefChange={this.handleSourceRefChange}
            onSubmit={this.handleRegisterSubmit}
          />
        ) : null}
        {hideTarget === null ? null : (
          <HideDatasetModal
            datasetName={hideTarget.displayName}
            errorMessage={hideErrorMessage}
            submitting={hideSubmitting}
            onCancel={this.closeHideDialog}
            onConfirm={this.handleHideConfirm}
          />
        )}
      </>
    );
  }

  private renderRowActions(dataset: Dataset): ReactNode {
    return (
      <DatasetRowActionsMenu
        dataset={dataset}
        onCopySourceRef={this.handleCopySourceRef}
        onHide={this.openHideDialog}
        onShow={this.handleShow}
      />
    );
  }

  private readonly handleRefresh = (): void => {
    void this.source.reload();
  };

  private readonly handleViewModeChange = (viewMode: ViewMode): void => {
    writeDatasetViewMode(viewMode);
    this.setState({ viewMode });
  };

  private readonly openRegisterDialog = (): void => {
    this.setState({ dialog: emptyRegisterDialog() });
  };

  private readonly closeRegisterDialog = (): void => {
    this.setState({ dialog: CLOSED_DIALOG });
  };

  private readonly handleSourceRefChange = (sourceRef: string): void => {
    this.setState((state) => {
      if (state.dialog.kind !== "register") {
        return state;
      }

      const { form } = state.dialog;
      return {
        ...state,
        dialog: {
          ...state.dialog,
          form: {
            ...form,
            sourceRef,
            // 사용자가 이름을 건드리기 전까지는 주소의 마지막 세그먼트를 따라간다.
            displayName: form.displayNameTouched ? form.displayName : suggestDisplayName(sourceRef),
          },
          fieldErrors: { ...state.dialog.fieldErrors, sourceRef: null },
        },
      };
    });
  };

  private readonly handleDisplayNameChange = (displayName: string): void => {
    this.setState((state) => {
      if (state.dialog.kind !== "register") {
        return state;
      }

      return {
        ...state,
        dialog: {
          ...state.dialog,
          form: { ...state.dialog.form, displayName, displayNameTouched: true },
          fieldErrors: { ...state.dialog.fieldErrors, displayName: null },
        },
      };
    });
  };

  private readonly handleRegisterSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const { dialog } = this.state;
    if (dialog.kind !== "register" || dialog.submitting) {
      return;
    }

    const check = checkSourceRef(dialog.form.sourceRef);
    const displayName = dialog.form.displayName.trim();
    const fieldErrors: RegisterFieldErrors = {
      sourceRef: check.ok ? null : check.message,
      displayName: displayName === "" ? "표시 이름을 입력해 주세요." : null,
    };
    if (!check.ok || fieldErrors.displayName !== null) {
      this.setState({ dialog: { ...dialog, fieldErrors, errorMessage: null } });
      return;
    }

    this.setState({
      dialog: {
        ...dialog,
        fieldErrors: EMPTY_REGISTER_FIELD_ERRORS,
        errorMessage: null,
        submitting: true,
      },
    });

    void registerDataset({ sourceRef: check.normalized, displayName })
      .then((outcome) => {
        // 201 과 200 은 사용자에게 서로 다른 일이다 — 새로 만든 것과 이미 있던 것.
        if (outcome.created) {
          toast.success("Dataset 을 등록했습니다.");
        } else {
          toast.info("이미 등록된 원본입니다. 기존 Dataset 을 엽니다.");
        }
        this.setState({ dialog: CLOSED_DIALOG });
        this.props.onOpenDataset(outcome.dataset);
      })
      .catch((error: unknown) => {
        this.setState((state) =>
          state.dialog.kind === "register"
            ? {
                ...state,
                dialog: {
                  ...state.dialog,
                  submitting: false,
                  errorMessage: resolveDatasetErrorMessage(error),
                },
              }
            : state,
        );
      });
  };

  private readonly handleCopySourceRef = (dataset: Dataset): void => {
    void navigator.clipboard
      .writeText(dataset.sourceRef)
      .then(() => toast.success("원본 주소를 복사했습니다."))
      .catch(() =>
        toast.warning("원본 주소를 복사하지 못했습니다. 값을 직접 선택해 복사해 주세요."),
      );
  };

  private readonly openHideDialog = (dataset: Dataset): void => {
    this.setState({ hideTarget: dataset, hideErrorMessage: null, hideSubmitting: false });
  };

  private readonly closeHideDialog = (): void => {
    this.setState({ hideTarget: null, hideErrorMessage: null, hideSubmitting: false });
  };

  private readonly handleHideConfirm = (): void => {
    const target = this.state.hideTarget;
    if (target === null || this.state.hideSubmitting) {
      return;
    }

    this.setState({ hideSubmitting: true, hideErrorMessage: null });
    void hideDataset(target.uuid)
      .then(async (dataset) => {
        toast.success("Dataset 을 목록에서 숨겼습니다.");
        this.setState({ hideTarget: null, hideSubmitting: false });
        await this.source.applyDataset(dataset);
      })
      .catch((error: unknown) => {
        this.setState({
          hideSubmitting: false,
          hideErrorMessage: resolveDatasetErrorMessage(error),
        });
      });
  };

  /** 되돌리는 동작이라 확인 없이 즉시 실행한다 — 잃는 것이 없다. */
  private readonly handleShow = (dataset: Dataset): void => {
    void showDataset(dataset.uuid)
      .then(async (next) => {
        toast.success("Dataset 을 다시 목록에 표시합니다.");
        await this.source.applyDataset(next);
      })
      .catch((error: unknown) => {
        toast.error(resolveDatasetErrorMessage(error));
      });
  };
}
